<!--
  - Copyright (c) 2022-2024. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
      ref="table"
      :custom-on-row-click="goto"
      :model="model"
      :use-add-action="false"
      :use-edit-action="false"
      entity-name="product"
      icon="mdi-food-fork-drink"
      @reloaded="infoReloaded"
      @selectedItems="selectedItem"
    >
      <template
        #item.dishName="{item}"
      >
        <div
          style="max-width: 210px;"
        >
          {{ item.dishName }}
        </div>
      </template>
      <template #primaryButton>
        <v-btn
          class="guide3Step3"
          color="amber lighten-4 black--text"
          elevation="0"
          @click="newAdd"
        >
          <div
            class="d-flex align-center"
          >
            <v-icon
              left
            >
              mdi-plus-circle-outline
            </v-icon>
            <template>
              <div
                class="text-truncate"
                style="max-width: 80px !important;"
              >
                {{ $t('newProduct') }}
              </div>
            </template>
          </div>
        </v-btn>
      </template>
      <template
        #footer
      >
        <template>
          <v-dialog
            v-model="showUploadDishDialog"
            :max-width="$vuetify.breakpoint.smAndUp? '60%' : ''"
            persistent
          >
            <template #activator="{on, attrs}">
              <v-btn
                :small="!$vuetify.breakpoint.mdAndUp"
                :style="!$vuetify.breakpoint.mdAndUp ? '' : 'background: white;'"
                class="ml-2"
                elevation="0"
                outlined
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  color="black"
                  left
                >
                  mdi-import
                </v-icon>
                {{ $t('bulkImport') }}
              </v-btn>
            </template>
            <v-card>
              <upload-page
                :file-type="'dish'"
                :title="$t('dishInfoExport')"
                @close="closeDialog"
                @successImport="closeDialog"
              />
            </v-card>
          </v-dialog>
        </template>
        <v-btn
          :small="!$vuetify.breakpoint.mdAndUp"
          :style="!$vuetify.breakpoint.mdAndUp ? '' : 'background: white;'"
          elevation="0"
          outlined
          @click="helpDialog=true"
        >
          <v-icon
            color="black"
            left
          >
            mdi-information-outline
          </v-icon>
          {{ $t('help') }}
        </v-btn>
        <v-btn
          v-if="printTagList.length > 0"
          :small="!$vuetify.breakpoint.mdAndUp"
          :style="!$vuetify.breakpoint.mdAndUp ? '' : 'background: white;'"
          elevation="0"
          outlined
          @click="printTag"
        >
          <v-icon
            color="black"
            left
          >
            mdi-cloud-print-outline
          </v-icon>
          {{ $t('print') }}
        </v-btn>
      </template>
    </ik-data-table>
    <v-dialog
      v-model="helpDialog"
      max-width="400px"
    >
      <v-card>
        <v-card-title>{{ $t('useInfo') }}</v-card-title>
        <v-card-text>
          <div class="text-h4">
            {{ $t('welcomeUseItems') }}
          </div>
          <div class="text-body-1 mt-4">
            <p>
              {{ $t('productHint1') }}
            </p>
            <p>
              {{ $t('productHint2') }}
            </p>
            <p>
              {{ $t('productHint3') }}
            </p>
            <p>
              {{ $t('productHint4') }}
            </p>
          </div>
        </v-card-text>
        <v-card-actions class="d-flex justify-center align-center absoluteBtn pb-4">
          <v-btn
            color="amber lighten-4 black--text"
            elevation="0"
            @click="read"
          >
            {{ $t('iUnderStand') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <global-listener @submit="enterPressed" />
    <v-dialog
      v-model="showEditDishDialog"
      :fullscreen="!$vuetify.breakpoint.mdAndUp"
      max-width="800px"
    >
      <v-card>
        <dishes-detail
          :out-side-list="dishList"
          v-if="editingDishId"
          :id="editingDishId"
          @close="closeEditDishDialog"
        />
      </v-card>
    </v-dialog>
    <add-new-dish-dialog
      v-model="showNewAddDialog"
      :new-product-code="editItemCode"
      @close="closeNewAddDialog"
    />
    <v-dialog v-model="showAllImageDialog">
      <v-card class="pa-4">
        <div
          style="display: grid;
        grid-template-columns: repeat(12,minmax(0,1fr));grid-gap: 12px"
        >
          <v-responsive
            v-for="dish in dishList.filter(it=>it.image&&!it.image.endsWith('.'))"
            :key="dish.id"
            :aspect-ratio="1"
          >
            <img
              :src="root+dish.image"
              width="100%"
            >
          </v-responsive>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showUploadImageDialog"
      max-width="700"
    >
      <v-card class="pa-4">
        <upload-image />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import Dish, { printDishesTag } from '@/model/dataLayer/api/dish/Dish'
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import AddNewDishDialog from '@/views/AadenPages/dishes/dish/AddNewDishDialog.vue'
import { keyBy } from 'lodash-es'
import GlobalListener from '@/views/AadenPages/dishes/dish/widget/GlobalListener.vue'
import DishesDetail from '@/views/AadenPages/dishes/dish/detail/DishesDetailPage.vue'
import UploadPage from '@/views/AadenPages/dishes/dish-display/UploadPage'
import UploadImage from '@/views/AadenPages/dishes/dish/widget/UploadImage.vue'
import GlobalSettings, { getRootUrl, Remember } from '@/model/global/GlobalSettings'

export default {
  name: 'DishesManagePage',
  components: {
    UploadImage,
    DishesDetail,
    GlobalListener,
    AddNewDishDialog,
    UploadPage,
    IkDataTable
  },
  data: function () {
    return {
      dishList: [],
      showUploadImageDialog: false,
      editItemCode: '',
      showAllImageDialog: false,
      editingDishId: null,
      newDishCode: null,
      showNewAddDialog: null,
      showUploadDishDialog: false,
      model: Dish,
      helpDialog: null,
      dishDict: {},
      showEditDishDialog: false,
      root: getRootUrl() + GlobalSettings.dishImageRoot,
      printTagList: []
    }
  },
  computed: {
    anyDialogOpen () {
      return this.showNewAddDialog || this.showUploadDishDialog
    }
  },
  watch: {
    showNewAddDialog (val) {
      if (!val) {
        this.editItemCode = ''
      }
    },
    showEditDishDialog (val) {
      if (!val) {
        this.editingDishId = null
      }
    }
  },
  async mounted () {
    if (!Remember.readItemHint) {
      this.helpDialog = true
    }
    await this.reload()
  },

  methods: {
    async printTag () {
      const list = this.printTagList.map(it => it.code).join(',')
      await printDishesTag(list)
      this.$refs.table.selectedItems = []
    },
    selectedItem (item) {
      this.printTagList = item
    },
    enterPressed (key) {
      if (!this.anyDialogOpen) {
        if (key === '/tu') {
          this.showAllImageDialog = true
        } else if (key === '/shang') {
          this.showUploadImageDialog = true
        } else {
          const res = this.dishDict[key.toLowerCase()]
          if (res) {
            this.goto(res)
          } else {
            this.showNewAddDialog = true
            this.editItemCode = key
          }
        }
      }
    },
    read () {
      Remember.readItemHint = true
      this.helpDialog = false
    },
    async infoReloaded (itemList) {
      const list = itemList.map(it => {
        it.dictKey = it.code.toLowerCase()
        return it
      })
      this.dishList = itemList
      this.dishDict = keyBy(list, 'dictKey')
    },
    async reload () {
      if (!this.$refs.table.loading) {
        this.dishList = []
        this.$refs.table.reload()
      }
    },
    async closeNewAddDialog (newDish) {
      this.showNewAddDialog = false
      if (newDish) {
        await this.reload()
        this.$refs.table.search = newDish.code
      }
    },
    async closeEditDishDialog () {
      this.showEditDishDialog = false
      this.editingDishId = null

      await this.reload()
    },
    newAdd () {
      this.showNewAddDialog = true
    },
    goto (item) {
      if (this.dishList.length > 0) {
        this.editingDishId = item.dishId
        this.showEditDishDialog = true
      }
    },

    closeDialog () {
      this.$refs.table.reload()
      this.showUploadDishDialog = false
    }
  }
}
</script>
